import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { EduShell, EmptyState, StatCard, StatusBadge, STUDENT_NAV } from "@/components/EduShell";
import { useAuth } from "@/hooks/useAuth";
import { eduRpc, formatDue, isOverdue } from "@/lib/education";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/assignments/")({
  component: StudentAssignments,
  head: () => ({
    meta: [
      { title: "My assignments - Inquira" },
      { name: "description", content: "See what is due, what you have submitted and the feedback your teacher left." },
      { property: "og:title", content: "My Inquira assignments" },
      { property: "og:description", content: "Due soon, submitted and graded work in one list." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface Overview {
  classes: Array<{ id: string; name: string }>;
  due_soon: number;
  submitted: number;
  average_score: number | null;
  assignments: Array<{
    id: string;
    title: string;
    class_name: string | null;
    kind: string | null;
    due_at: string | null;
    status: string;
    score: number | null;
  }>;
}


const TABS = ["todo", "submitted", "graded"] as const;

function StudentAssignments() {
  const { user } = useAuth();
  const [data, setData] = useState<Overview | null>(null);
  const [tab, setTab] = useState<(typeof TABS)[number]>("todo");

  useEffect(() => {
    if (!user) return;
    void eduRpc<Overview>("edu_student_overview").then(({ data }) => setData(data));
  }, [user]);

  if (!user) {
    return (
      <EduShell nav={STUDENT_NAV} title={i18n.t("edu.assignments_index.my_assignments")}>
        <EmptyState title={i18n.t("edu.assignments_index.sign_in_first")} body="Sign in to see the work your teachers assigned." />
        <div className="mt-4">
          <Link to="/login" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><T k="edu.assignments_index.sign_in" /></Link>
        </div>
      </EduShell>
    );
  }

  const all = data?.assignments ?? [];
  const visible = all.filter((a) => {
    if (tab === "todo") return ["not_started", "in_progress"].includes(a.status);
    if (tab === "submitted") return ["submitted", "late"].includes(a.status);
    return ["graded", "returned"].includes(a.status);
  });

  return (
    <EduShell nav={STUDENT_NAV} title={i18n.t("edu.assignments_index.my_assignments")} subtitle={i18n.t("edu.assignments_index.everything_your_teachers_assigned_newest")}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label={i18n.t("edu.assignments_index.classes")} value={data ? data.classes.length : "—"} />
        <StatCard label={i18n.t("edu.assignments_index.due_soon")} value={data?.due_soon ?? "—"} />
        <StatCard label={i18n.t("edu.assignments_index.submitted")} value={data?.submitted ?? "—"} />
        <StatCard label={i18n.t("edu.assignments_index.average_score")} value={data?.average_score ?? "—"} />
      </div>

      <div className="my-5 flex gap-1 text-sm">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-3 py-1.5 font-semibold capitalize ${
              tab === t ? "border border-rule bg-card text-ink" : "text-ink-muted"
            }`}
          >
            {t === "todo" ? "To do" : t}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState title={i18n.t("edu.assignments_index.nothing_here")} body="Join a class or check another tab." />
      ) : (
        <ul className="space-y-2">
          {visible.map((a) => (
            <li key={a.id} className="rounded-2xl border border-rule bg-card p-4">
              <Link to="/assignments/$assignmentId" params={{ assignmentId: a.id }} className="block">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-ink">{a.title}</span>
                  <span className="flex items-center gap-2 text-xs">
                    <StatusBadge status={a.status} />
                    <span className={isOverdue(a.due_at, a.status) ? "font-semibold text-destructive" : "text-ink-muted"}>
                      {formatDue(a.due_at)}
                    </span>
                  </span>
                </div>
                <p className="mt-1 text-sm text-ink-muted">
                  {[a.class_name, a.kind, a.score !== null ? `Score ${a.score}` : null].filter(Boolean).join(" · ")}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </EduShell>
  );
}
